import type { CSSProperties } from "react";
import {
  ArrowRight,
  ChevronRight,
  Gamepad2,
  Keyboard,
  Mic,
  Moon,
  Move,
  Smartphone,
  Sun,
  Tv,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteFooter } from "@/components/landing/SiteFooter";
import { SiteHeader } from "@/components/landing/SiteHeader";
import { useSite } from "@/hooks/use-site";
import { cn } from "@/lib/utils";
import { localizeUrl, type Locale } from "@/lib/site";
import appleEnBadge from "@/assets/remote-app/badges/apple/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg";
import applePlBadge from "@/assets/remote-app/badges/apple/Download_on_the_App_Store_Badge_PL_RGB_blk_100317.svg";
import appleUkBadge from "@/assets/remote-app/badges/apple/Download_on_the_App_Store_Badge_UA_blk_082124.svg";
import googleEnBadge from "@/assets/remote-app/badges/google/en.svg";
import googlePlBadge from "@/assets/remote-app/badges/google/pl.svg";
import googleUkBadge from "@/assets/remote-app/badges/google/uk.svg";
import heroLifestyle from "@/assets/remote-app/lifestyle/inext-remote-app-hero-lifestyle.jpg";
import gamepadLifestyle from "@/assets/remote-app/lifestyle/inext-remote-app-gamepad-lifestyle.jpg";
import enControl from "@/assets/remote-app/screenshots/en/inext-remote-app-control-screen.png";
import enDevices from "@/assets/remote-app/screenshots/en/inext-remote-app-device-switching.png";
import enDialpad from "@/assets/remote-app/screenshots/en/inext-remote-app-dialpad.png";
import enGamepad from "@/assets/remote-app/screenshots/en/inext-remote-app-gamepad-mode.png";
import enKeyboard from "@/assets/remote-app/screenshots/en/inext-remote-app-keyboard-input.png";
import enThemes from "@/assets/remote-app/screenshots/en/inext-remote-app-light-dark-themes.png";
import ukControl from "@/assets/remote-app/screenshots/uk/inext-remote-app-control-screen-uk.png";
import ukDevices from "@/assets/remote-app/screenshots/uk/inext-remote-app-device-switching-uk.png";
import ukDialpad from "@/assets/remote-app/screenshots/uk/inext-remote-app-dialpad-uk.png";
import ukGamepad from "@/assets/remote-app/screenshots/uk/inext-remote-app-gamepad-mode-uk.png";
import ukKeyboard from "@/assets/remote-app/screenshots/uk/inext-remote-app-keyboard-input-uk.png";
import ukThemes from "@/assets/remote-app/screenshots/uk/inext-remote-app-light-dark-themes-uk.png";

const APP_STORE_URL = "https://apps.apple.com/us/app/inext-remote/id6444906559";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=ua.inext.tvremote&hl=en";

const demoVideoIds: Record<Locale, string> = {
  en: "cGoGoDZIGAU",
  uk: "e3IuJ4QXMqs",
  pl: "cGoGoDZIGAU",
  // TODO(pt-BR): swap to a Portuguese demo video if one is produced.
  pt: "cGoGoDZIGAU",
  // es (Latin America) reuses the English demo video.
  es: "cGoGoDZIGAU",
};

const badges: Record<Locale, { apple: string; google: string }> = {
  en: { apple: appleEnBadge, google: googleEnBadge },
  uk: { apple: appleUkBadge, google: googleUkBadge },
  pl: { apple: applePlBadge, google: googlePlBadge },
  // TODO(pt-BR): use Portuguese store badges if added; English for now.
  pt: { apple: appleEnBadge, google: googleEnBadge },
  // es (Latin America) intentionally reuses the English store badges.
  es: { apple: appleEnBadge, google: googleEnBadge },
};

const screenshots = {
  en: [
    { src: enControl, wide: false },
    { src: enGamepad, wide: true },
    { src: enKeyboard, wide: false },
    { src: enDevices, wide: false },
    { src: enDialpad, wide: false },
    { src: enThemes, wide: false },
  ],
  uk: [
    { src: ukControl, wide: false },
    { src: ukGamepad, wide: true },
    { src: ukKeyboard, wide: false },
    { src: ukDevices, wide: false },
    { src: ukDialpad, wide: false },
    { src: ukThemes, wide: false },
  ],
  pl: [
    { src: enControl, wide: false },
    { src: enGamepad, wide: true },
    { src: enKeyboard, wide: false },
    { src: enDevices, wide: false },
    { src: enDialpad, wide: false },
    { src: enThemes, wide: false },
  ],
  // TODO(pt-BR): use Portuguese screenshots if captured; English for now.
  pt: [
    { src: enControl, wide: false },
    { src: enGamepad, wide: true },
    { src: enKeyboard, wide: false },
    { src: enDevices, wide: false },
    { src: enDialpad, wide: false },
    { src: enThemes, wide: false },
  ],
  // es (Latin America) reuses the English app screenshots.
  es: [
    { src: enControl, wide: false },
    { src: enGamepad, wide: true },
    { src: enKeyboard, wide: false },
    { src: enDevices, wide: false },
    { src: enDialpad, wide: false },
    { src: enThemes, wide: false },
  ],
} satisfies Record<Locale, Array<{ src: string; wide: boolean }>>;

function StoreBadges({
  locale,
  appleAlt,
  googleAlt,
}: {
  locale: Locale;
  appleAlt: string;
  googleAlt: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={appleAlt}
        className="transition-transform duration-200 hover:-translate-y-0.5"
      >
        <img src={badges[locale].apple} alt={appleAlt} className="h-11 w-auto sm:h-12" />
      </a>
      <a
        href={GOOGLE_PLAY_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={googleAlt}
        className="transition-transform duration-200 hover:-translate-y-0.5"
      >
        <img src={badges[locale].google} alt={googleAlt} className="h-11 w-auto sm:h-12" />
      </a>
    </div>
  );
}

function PhoneFrame({
  src,
  alt,
  className,
  style,
  loading,
}: {
  src: string;
  alt: string;
  className?: string;
  style?: CSSProperties;
  loading?: "lazy" | "eager";
}) {
  return (
    <div
      style={style}
      className={cn("rounded-[2rem] border border-border bg-foreground p-2", className)}
    >
      <div className="overflow-hidden rounded-[1.55rem] bg-background">
        <img
          src={src}
          alt={alt}
          loading={loading}
          className="block aspect-[9/19.5] h-auto w-full object-cover"
        />
      </div>
    </div>
  );
}

export function RemoteAppPage() {
  const { messages, site, href } = useSite();
  const home = localizeUrl("/", site.locale, href);
  const contactSales = localizeUrl("/contacts?inquiry=sales#inquiry-form", site.locale, href);
  const localeScreenshots = screenshots[site.locale];
  const demoVideoId = demoVideoIds[site.locale];
  const reveal = (delay: number) => ({ animationDelay: `${delay}ms` });

  const stats = [
    [messages.remoteAppStatDownloadsValue, messages.remoteAppStatDownloadsLabel],
    [messages.remoteAppStatModesValue, messages.remoteAppStatModesLabel],
    [messages.remoteAppStatThemesValue, messages.remoteAppStatThemesLabel],
  ];

  const features = [
    {
      Icon: Tv,
      title: messages.remoteAppFeatureControlTitle,
      body: messages.remoteAppFeatureControlBody,
    },
    {
      Icon: Smartphone,
      title: messages.remoteAppFeatureRoomsTitle,
      body: messages.remoteAppFeatureRoomsBody,
    },
    {
      Icon: Mic,
      title: messages.remoteAppFeatureVoiceTitle,
      body: messages.remoteAppFeatureVoiceBody,
    },
    {
      Icon: Keyboard,
      title: messages.remoteAppFeatureKeyboardTitle,
      body: messages.remoteAppFeatureKeyboardBody,
    },
    {
      Icon: Gamepad2,
      title: messages.remoteAppFeatureGamepadTitle,
      body: messages.remoteAppFeatureGamepadBody,
    },
    {
      Icon: Move,
      title: messages.remoteAppFeatureModesTitle,
      body: messages.remoteAppFeatureModesBody,
    },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteHeader />

      <main className="flex-1 overflow-x-hidden">
        {/* Breadcrumb */}
        <div className="border-b border-border/60 bg-background/60">
          <nav
            aria-label={messages.breadcrumbLabel}
            className="mx-auto flex max-w-7xl items-center gap-1.5 px-6 py-3 text-xs text-muted-foreground"
          >
            <a href={home} className="hover:text-foreground">
              {messages.tv6UltraBreadcrumbHome}
            </a>
            <ChevronRight aria-hidden className="h-3 w-3" />
            <a
              href={localizeUrl("/solutions", site.locale, href)}
              className="hover:text-foreground"
            >
              {messages.remoteAppBreadcrumbApps}
            </a>
            <ChevronRight aria-hidden className="h-3 w-3" />
            <span className="text-foreground">inext Remote</span>
          </nav>
        </div>

        {/* Hero */}
        <section className="relative overflow-hidden border-b border-border/60">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 -z-10 opacity-70"
            style={{ background: "var(--gradient-aurora)" }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 -z-10 bg-grid opacity-40"
          />

          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-16 lg:grid-cols-[1fr_0.9fr] lg:items-center lg:py-24">
            <div>
              <div className="inline-flex animate-fade-up items-center gap-2 rounded-full border border-primary/30 bg-card/60 px-3 py-1 text-xs font-medium text-foreground backdrop-blur">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                {messages.remoteAppHeroEyebrow}
              </div>
              <h1 className="mt-5 max-w-2xl animate-fade-up text-balance text-4xl font-semibold leading-[1.05] tracking-tight text-foreground sm:text-6xl">
                {messages.remoteAppHeroTitle}
              </h1>
              <p
                className="mt-6 max-w-xl animate-fade-up text-lg leading-relaxed text-muted-foreground"
                style={reveal(120)}
              >
                {messages.remoteAppHeroBody}
              </p>

              <div className="mt-8 animate-fade-up" style={reveal(180)}>
                <StoreBadges
                  locale={site.locale}
                  appleAlt={messages.remoteAppAppleBadgeAlt}
                  googleAlt={messages.remoteAppGoogleBadgeAlt}
                />
              </div>

              <dl
                className="mt-10 grid max-w-2xl animate-fade-up grid-cols-3 gap-3"
                style={reveal(240)}
              >
                {stats.map(([value, label]) => (
                  <div
                    key={label}
                    className="rounded-xl border border-border bg-card/50 px-4 py-4 backdrop-blur transition-colors duration-300 hover:border-primary/40"
                  >
                    <dt className="text-xl font-semibold tracking-tight text-foreground">
                      {value}
                    </dt>
                    <dd className="mt-1 text-[0.7rem] uppercase tracking-wider text-muted-foreground">
                      {label}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            <div
              className="relative mx-auto w-full max-w-[26rem] animate-fade-up lg:max-w-[30rem]"
              style={reveal(120)}
            >
              <div
                aria-hidden
                className="pointer-events-none absolute -inset-10 -z-10 rounded-full opacity-70 blur-3xl"
                style={{ background: "var(--gradient-aurora)" }}
              />
              <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-glow">
                <img
                  src={heroLifestyle}
                  alt={messages.remoteAppHeroImageAlt}
                  className="block h-auto w-full object-cover"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Demo video */}
        <section className="border-b border-border/60 bg-muted/15">
          <div className="mx-auto max-w-5xl px-6 py-20">
            <div className="mx-auto max-w-2xl animate-fade-up text-center">
              <p className="text-sm font-medium uppercase tracking-wider text-primary">
                {messages.remoteAppVideoEyebrow}
              </p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
                {messages.remoteAppVideoTitle}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                {messages.remoteAppVideoBody}
              </p>
            </div>
            <div className="relative mt-10 animate-fade-up" style={reveal(120)}>
              <div
                aria-hidden
                className="pointer-events-none absolute -inset-8 -z-10 rounded-[2rem] opacity-50 blur-3xl"
                style={{ background: "var(--gradient-aurora)" }}
              />
              <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-glow">
                <div className="aspect-video">
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${demoVideoId}?rel=0`}
                    title={messages.remoteAppVideoIframeTitle}
                    className="h-full w-full"
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="border-b border-border/60 bg-card/40">
          <div className="mx-auto max-w-7xl px-6 py-20">
            <div className="max-w-3xl animate-fade-up">
              <p className="text-sm font-medium uppercase tracking-wider text-primary">
                {messages.remoteAppFeaturesEyebrow}
              </p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
                {messages.remoteAppFeaturesTitle}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                {messages.remoteAppFeaturesBody}
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {features.map(({ Icon, title, body }, index) => (
                <article
                  key={title}
                  style={reveal(index * 60)}
                  className="group animate-fade-up rounded-2xl border border-border bg-background p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-[var(--shadow-elevated)]"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors duration-300 group-hover:bg-primary group-hover:text-primary-foreground">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 text-base font-semibold text-foreground">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Modes — gamepad */}
        <section className="relative overflow-hidden border-b border-border/60">
          <div
            aria-hidden
            className="pointer-events-none absolute -left-32 -top-24 -z-10 h-[26rem] w-[26rem] rounded-full opacity-40 blur-3xl"
            style={{ background: "var(--gradient-aurora)" }}
          />
          <div className="mx-auto max-w-7xl px-6 py-20">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
              <div className="relative animate-fade-up">
                <div
                  aria-hidden
                  className="pointer-events-none absolute -inset-8 -z-10 rounded-[2rem] opacity-60 blur-3xl"
                  style={{ background: "var(--gradient-aurora)" }}
                />
                <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-glow">
                  <img
                    src={gamepadLifestyle}
                    alt={messages.remoteAppGamepadImageAlt}
                    className="block aspect-[16/9] w-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>

              <div className="animate-fade-up" style={reveal(120)}>
                <p className="text-sm font-medium uppercase tracking-wider text-primary">
                  {messages.remoteAppModesEyebrow}
                </p>
                <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
                  {messages.remoteAppModesTitle}
                </h2>
                <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                  {messages.remoteAppModesBody}
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {[messages.remoteAppModeDpad, messages.remoteAppModeSwipe].map((mode) => (
                    <span
                      key={mode}
                      className="rounded-full border border-border/70 bg-card/60 px-3 py-1.5 text-xs text-foreground/90 backdrop-blur"
                    >
                      {mode}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Screenshots gallery */}
        <section className="border-b border-border/60 bg-muted/15">
          <div className="mx-auto max-w-7xl px-6 py-20">
            <div className="flex flex-col gap-4 animate-fade-up sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-sm font-medium uppercase tracking-wider text-primary">
                  {messages.remoteAppScreensEyebrow}
                </p>
                <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
                  {messages.remoteAppScreensTitle}
                </h2>
              </div>
              <p className="max-w-xl text-sm leading-relaxed text-muted-foreground">
                {messages.remoteAppScreensBody}
              </p>
            </div>
          </div>

          {/* Uniform phone mockups — centered on desktop, snap-scroll on mobile */}
          <div className="pb-20">
            <div className="flex snap-x snap-mandatory gap-5 overflow-x-auto px-6 pt-2 pb-4 [-ms-overflow-style:none] [scrollbar-width:none] xl:justify-center [&::-webkit-scrollbar]:hidden">
              {localeScreenshots
                .filter((shot) => !shot.wide)
                .map((shot, index) => (
                  <PhoneFrame
                    key={shot.src}
                    src={shot.src}
                    alt={`${messages.remoteAppScreenshotAlt} ${index + 1}`}
                    loading="lazy"
                    style={reveal(index * 80)}
                    className="w-44 shrink-0 animate-fade-up snap-center shadow-[var(--shadow-elevated)] transition-transform duration-300 hover:-translate-y-2 sm:w-48 lg:w-52"
                  />
                ))}
            </div>
          </div>
        </section>

        {/* Themes */}
        <section className="border-b border-border/60">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-6 py-20 lg:grid-cols-[1fr_0.9fr] lg:items-center">
            <div className="animate-fade-up">
              <p className="text-sm font-medium uppercase tracking-wider text-primary">
                {messages.remoteAppThemesEyebrow}
              </p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
                {messages.remoteAppThemesTitle}
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                {messages.remoteAppThemesBody}
              </p>
            </div>
            <div
              className="grid grid-cols-1 gap-4 animate-fade-up sm:grid-cols-2"
              style={reveal(120)}
            >
              <div className="rounded-2xl border border-border bg-card p-6 transition-shadow duration-300 hover:shadow-[var(--shadow-elevated)]">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Sun className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-base font-semibold">{messages.remoteAppLightTheme}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {messages.remoteAppLightThemeBody}
                </p>
              </div>
              <div className="rounded-2xl border border-border bg-foreground p-6 text-background transition-shadow duration-300 hover:shadow-glow">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-background/10 text-background">
                  <Moon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-base font-semibold">{messages.remoteAppDarkTheme}</h3>
                <p className="mt-2 text-sm leading-relaxed text-background/70">
                  {messages.remoteAppDarkThemeBody}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative overflow-hidden bg-foreground text-background">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-40"
            style={{ background: "var(--gradient-aurora)" }}
          />
          <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-8 px-6 py-16 lg:grid-cols-[1fr_auto] lg:items-center">
            <div className="animate-fade-up">
              <p className="text-sm font-medium text-background/70">
                {messages.remoteAppCtaEyebrow}
              </p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
                {messages.remoteAppCtaTitle}
              </h2>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-background/70">
                {messages.remoteAppCtaBody}
              </p>
            </div>
            <div className="flex flex-col gap-4 animate-fade-up" style={reveal(120)}>
              <StoreBadges
                locale={site.locale}
                appleAlt={messages.remoteAppAppleBadgeAlt}
                googleAlt={messages.remoteAppGoogleBadgeAlt}
              />
              <Button asChild variant="secondary" className="h-11 rounded-lg px-5 font-medium">
                <a href={contactSales}>
                  {messages.contactSales}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
